import { useRef, useState } from 'react';
import { Upload, X, ImagePlus, Link as LinkIcon } from 'lucide-react';

interface Props {
  images: string[];
  onChange: (images: string[]) => void;
  maxImages?: number;
  label?: string;
}

export function ImageUploader({ images, onChange, maxImages = 10, label = 'Images' }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [urlMode, setUrlMode] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    setUploading(true);
    const newImages: string[] = [];

    for (let i = 0; i < files.length && images.length + newImages.length < maxImages; i++) {
      const file = files[i]!;
      // Convert to base64 data URL (works without server upload)
      const dataUrl = await fileToDataUrl(file);
      newImages.push(dataUrl);
    }

    onChange([...images, ...newImages]);
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const addUrl = () => {
    const url = urlInput.trim();
    if (url && !images.includes(url) && images.length < maxImages) {
      onChange([...images, url]);
      setUrlInput('');
    }
  };

  const removeImage = (idx: number) => {
    onChange(images.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-gray-700">{label}</label>

      {/* Image previews */}
      {images.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {images.map((url, idx) => (
            <div key={idx} className="group relative h-20 w-20 overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
              <img
                src={url}
                alt={`Image ${idx + 1}`}
                className="h-full w-full object-cover"
                onError={(e) => {
                  const el = e.target as HTMLImageElement;
                  el.style.display = 'none';
                }}
              />
              <button
                type="button"
                onClick={() => removeImage(idx)}
                className="absolute right-0.5 top-0.5 rounded-full bg-red-500 p-0.5 text-white opacity-0 transition-opacity group-hover:opacity-100"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload controls */}
      {images.length < maxImages && (
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2 rounded-lg border border-dashed border-gray-300 px-4 py-2.5 text-sm text-gray-600 transition-colors hover:border-[var(--ink-3)] hover:bg-[var(--saffron-50)] hover:text-[var(--ink-0)]"
            >
              <Upload size={16} />
              {uploading ? 'Uploading...' : 'Upload Image'}
            </button>
            <button
              type="button"
              onClick={() => setUrlMode(!urlMode)}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm transition-colors ${urlMode ? 'border-[var(--ink-3)] bg-[var(--saffron-50)] text-[var(--ink-0)]' : 'border-gray-300 text-gray-500 hover:text-gray-700'}`}
            >
              <LinkIcon size={14} />
              URL
            </button>
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />

          {urlMode && (
            <div className="flex gap-2">
              <input
                type="url"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addUrl(); } }}
                placeholder="https://example.com/image.jpg"
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
              />
              <button
                type="button"
                onClick={addUrl}
                disabled={!urlInput.trim()}
                className="rounded-lg bg-[var(--ink-0)] px-3 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-40"
              >
                Add
              </button>
            </div>
          )}

          <p className="text-xs text-gray-400">
            {images.length}/{maxImages} images. Supports JPG, PNG, WebP.
          </p>
        </div>
      )}
    </div>
  );
}

const MAX_WIDTH = 1200;
const MAX_HEIGHT = 1200;
const QUALITY = 0.75;

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = () => {
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;

        // Scale down if larger than max dimensions
        if (width > MAX_WIDTH || height > MAX_HEIGHT) {
          const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0, width, height);

        // Compress to WebP if supported, fallback to JPEG
        const compressed = canvas.toDataURL('image/webp', QUALITY) || canvas.toDataURL('image/jpeg', QUALITY);
        resolve(compressed);
      };
      img.onerror = reject;
      img.src = reader.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
